import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import { FilterIcon } from "lucide-react";
import { useMemo } from "react";
import { reportQueries } from "../../../queries";
import { useTenant } from "@/domains/tenant/hooks";

type FilterDropdownProps = {
  selectedTypes: string[];
  toggleType: (type: string, enabled: boolean) => void;
  clearTypes: () => void;
  globalTypes?: string[];
  reportTypes?: string[];
};

const emptyGlobalTypes: string[] = [];
const emptyReportTypes: string[] = [];
const formatTypeLabel = (value: string) =>
  (value || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase()) || "Unknown";

export const FilterDropdown = ({
  selectedTypes,
  toggleType,
  clearTypes,
  globalTypes,
  reportTypes,
}: FilterDropdownProps) => {
  const { data: tenantData } = useTenant();
  const resolvedGlobalTypes = globalTypes ?? emptyGlobalTypes;
  const resolvedReportTypes = reportTypes ?? emptyReportTypes;
  const { data: templateTypes = [] } = useQuery(
    reportQueries.templateTypes(tenantData?.tenant.tenant_id),
  );
  const orderedTypes = useMemo(() => {
    const globalTypeSet = new Set(resolvedGlobalTypes);
    const mergedTypes = Array.from(
      new Set(
        [...templateTypes, ...resolvedReportTypes, ...selectedTypes].filter(
          (type): type is string => typeof type === "string",
        ),
      ),
    );
    return [
      ...mergedTypes.filter((type) => !globalTypeSet.has(type)),
      ...mergedTypes.filter((type) => globalTypeSet.has(type)),
    ];
  }, [templateTypes, selectedTypes, resolvedReportTypes, resolvedGlobalTypes]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-1.5">
          <FilterIcon className="size-4" />
          Filters
          {selectedTypes.length > 0 && (
            <span className="text-muted-foreground ml-1 text-xs">
              ({selectedTypes.length})
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>Template Report Types</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {selectedTypes.length > 0 && (
            <DropdownMenuItem onClick={clearTypes}>
              Clear all filters
            </DropdownMenuItem>
          )}
          {orderedTypes.map((type) => (
            <DropdownMenuCheckboxItem
              key={type}
              checked={selectedTypes.includes(type)}
              onCheckedChange={(checked) => toggleType(type, checked === true)}
              onSelect={(e) => e.preventDefault()}
              className="capitalize"
            >
              {formatTypeLabel(type)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
